import React, { useState } from "react";
import { useAppDispatch } from "@/hooks/redux";
import { deleteUser } from "@/store/slices/adminSlice";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Trash2, AlertTriangle } from "lucide-react";
import type { AdminUser } from "./UsersDataTable";
import {
  formatSubscriptionStatus,
  getSubscriptionStatusColor,
} from "@/utils/subscription-status";

interface DeleteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: AdminUser | null;
  onSuccess?: () => void;
}

export const DeleteUserDialog: React.FC<DeleteUserDialogProps> = ({
  open,
  onOpenChange,
  user,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  if (!user) return null;

  const handleConfirm = async () => {
    try {
      setLoading(true);

      await dispatch(deleteUser(user.id)).unwrap();
      toast.success("Utilisateur supprimé avec succès");

      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Erreur lors de la suppression de l'utilisateur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2 text-red-600 dark:text-red-400">
            <AlertTriangle className="h-5 w-5" />
            <span>Supprimer l'utilisateur</span>
          </DialogTitle>
          <DialogDescription>
            Cette action est irréversible. L'utilisateur et toutes ses données
            (conversations, historique, etc.) seront définitivement supprimés du
            système.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-red-800 dark:text-red-200">
                {user.firstName && user.lastName
                  ? `${user.firstName} ${user.lastName}`
                  : user.email}
              </h4>
              <Badge
                className={getSubscriptionStatusColor(user.subscriptionStatus)}
              >
                {formatSubscriptionStatus(user.subscriptionStatus)}
              </Badge>
            </div>
            <div className="space-y-1 text-sm">
              <p className="text-red-600 dark:text-red-300">
                <strong>Email:</strong> {user.email}
              </p>
              <p className="text-red-600 dark:text-red-300">
                <strong>Plan:</strong> {user.subscriptionPlan}
              </p>
              <p className="text-red-600 dark:text-red-300">
                <strong>Inscrit le:</strong>{" "}
                {new Date(user.createdAt).toLocaleDateString("fr-FR")}
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <div className="flex items-start space-x-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-yellow-800 dark:text-yellow-200">
                  Attention
                </p>
                <p className="text-yellow-700 dark:text-yellow-300">
                  Cette action supprimera définitivement toutes les données
                  associées à cet utilisateur.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Suppression...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer définitivement
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
